import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { CoreApp } from '@grafana/data';
import { config } from '@grafana/runtime';
import { DataQuery } from '@grafana/schema';
import { QueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';

import { type OnSelectQueryType } from '../../features/explore/QueryLibrary/types';

import { AddToQueryLibraryModal } from './AddToQueryLibraryModal';
import { QueryLibraryInteractions } from './QueryLibraryAnalyticsEvents';
import { QueryLibraryDrawer } from './QueryLibraryDrawer';
import { SaveQueryButton } from './SaveQueryButton';

import { getQueryLibraryRenderContext } from './index';

export function QueryLibraryContextProvider({ children }: PropsWithChildren) {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [activeDatasources, setActiveDatasources] = useState<string[]>([]);
  const [isAddQueryModalOpen, setIsAddQueryModalOpen] = useState<boolean>(false);
  const [activeQuery, setActiveQuery] = useState<DataQuery | undefined>(undefined);
  const [title, setTitle] = useState<string | undefined>(undefined);
  // need to curry the no-op due to the special setState((prevState) => {}) pattern
  const [onSelectQuery, setOnSelectQuery] = useState<OnSelectQueryType>(() => () => {});
  const [context, setContext] = useState('unknown');
  const [onSave, setOnSave] = useState<(() => void) | undefined>(undefined);

  // Enhanced drawer options
  const [highlightedQuery, setHighlightedQuery] = useState<string | undefined>(undefined);

  // Auto-clear highlight after 3 seconds
  useEffect(() => {
    if (highlightedQuery) {
      const timer = setTimeout(() => {
        setHighlightedQuery(undefined);
      }, 2000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [highlightedQuery]);

  const openDrawer = useCallback(
    (
      datasourceFilters: string[],
      onSelectQuery: OnSelectQueryType,
      options?: {
        isReplacingQuery?: boolean;
        context?: string;
        highlightQuery?: string;
      }
    ) => {
      setActiveDatasources(datasourceFilters);
      // need to curry our function due to the special setState((prevState) => {}) pattern
      setOnSelectQuery(() => onSelectQuery);
      setIsDrawerOpen(true);
      setContext(getQueryLibraryRenderContext(options?.context));

      // Set enhanced options
      setHighlightedQuery(options?.highlightQuery);

      QueryLibraryInteractions.queryLibraryOpened();
      options?.isReplacingQuery
        ? QueryLibraryInteractions.replaceWithQueryFromLibraryClicked()
        : QueryLibraryInteractions.addQueryFromLibraryClicked();
    },
    []
  );

  const closeDrawer = useCallback((isSelectingQuery?: boolean) => {
    setActiveDatasources([]);
    // need to curry the no-op due to the special setState((prevState) => {}) pattern
    setOnSelectQuery(() => () => {});
    setIsDrawerOpen(false);

    // Clear enhanced options
    setHighlightedQuery(undefined);

    if (!isSelectingQuery) {
      QueryLibraryInteractions.queryLibraryClosedWithoutSelection();
    }
  }, []);

  const openAddQueryModal = useCallback(
    (
      query: DataQuery,
      options?: { isDuplicating?: boolean; onSave?: () => void; context?: string; title?: string }
    ) => {
      setActiveQuery(query);
      setIsAddQueryModalOpen(true);
      setContext(getQueryLibraryRenderContext(options?.context));
      setTitle(options?.title);
      setOnSave(options?.onSave);
      options?.isDuplicating
        ? QueryLibraryInteractions.duplicateQueryClicked()
        : QueryLibraryInteractions.saveQueryToLibraryClicked();
    },
    []
  );

  const closeAddQueryModal = useCallback(() => {
    setActiveQuery(undefined);
    setIsAddQueryModalOpen(false);
    setOnSave(undefined);
  }, []);

  const contextVal = useMemo(
    () => ({
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openAddQueryModal,
      closeAddQueryModal,
      renderSaveQueryButton: (query: DataQuery, app?: CoreApp) => <SaveQueryButton query={query} app={app} />,
      queryLibraryEnabled: Boolean(config.featureToggles.queryLibrary),
      context,
    }),
    [isDrawerOpen, openDrawer, closeDrawer, openAddQueryModal, closeAddQueryModal, context]
  );

  return (
    <QueryLibraryContext.Provider value={contextVal}>
      {children}
      <QueryLibraryDrawer
        isOpen={isDrawerOpen}
        close={() => closeDrawer(false)}
        activeDatasources={activeDatasources}
        highlightedQuery={highlightedQuery}
        onSelectQuery={(query) => {
          onSelectQuery(query);
          closeDrawer(true);
          QueryLibraryInteractions.selectQueryClicked();
        }}
      />
      <AddToQueryLibraryModal
        isOpen={isAddQueryModalOpen}
        close={closeAddQueryModal}
        query={activeQuery}
        onSave={onSave}
        context={context}
        title={title}
      />
    </QueryLibraryContext.Provider>
  );
}
